import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/Layout';
import { LoadingState } from '@/components/EmptyState';
import { useAuth } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import { LoginPage } from '@/pages/Login';
import { RequestResetPage, SetPasswordPage } from '@/pages/ResetPassword';
import { DashboardPage } from '@/pages/Dashboard';
import { InventoryPage } from '@/pages/Inventory';
import { ProductsPage } from '@/pages/Products/Products';
import { OrdersListPage } from '@/pages/Orders/List';
import { OrderDetailPage } from '@/pages/Orders/Detail';
import { ReturnsListPage } from '@/pages/Returns/List';
import { ReturnDetailPage } from '@/pages/Returns/Detail';
import { BillingPage } from '@/pages/Billing/Overview';
import { IntegrationsPage } from '@/pages/Integrations/Integrations';
import { RequestForm } from './pages/Requests/RequestForm';
import { InboundListPage } from './pages/Requests/InboundList';
import { InboundDetailPage } from './pages/Requests/InboundDetail';
import { RequestEditPage } from './pages/Requests/RequestEdit';

/** The old "Orders" URLs keep working: they land in the merged "Shipments" section (same id). */
function RedirectToShipment(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/shipments/${id}`} replace />;
}

function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { state } = useAuth();
  const t = useT();
  if (state.status === 'loading') return <LoadingState label={t('nav.checkingSession')} />;
  if (state.status !== 'authenticated') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). Six seller-facing sections
 * (Dashboard, Inventory, Receiving, Shipments, Returns, Billing) plus the
 * four auth-adjacent public routes (login, forgot/reset password, invite) -
 * the full screen set the epic named. No Settings/Users/Imports/Channels
 * section: those are internal Ops concerns with no seller-portal
 * equivalent in this epic's scope.
 *
 * `/invite` MUST be a top-level route registered here, outside `RequireAuth`
 * - it previously had none at all, so an unauthenticated seller opening
 * their invite link fell through to the layout route's own `path="*"`
 * catch-all below (nested under RequireAuth) and got silently redirected to
 * /login instead of ever seeing a set-password form. Same confirm flow as
 * /reset-password (SetPasswordPage's own docstring), different copy only.
 */
export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<RequestResetPage />} />
      <Route path="/reset-password" element={<SetPasswordPage variant="reset" />} />
      <Route path="/invite" element={<SetPasswordPage variant="invite" />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="inbound" element={<InboundListPage />} />
        <Route path="inbound/new" element={<RequestForm kind="INBOUND" />} />
        <Route path="inbound/:id" element={<InboundDetailPage />} />
        <Route path="inbound/:id/edit" element={<RequestEditPage kind="INBOUND" />} />
        <Route path="inbound/:id/revise" element={<RequestEditPage kind="INBOUND" revise />} />
        <Route path="shipments" element={<OrdersListPage />} />
        <Route path="shipments/new" element={<RequestForm kind="OUTBOUND" />} />
        <Route path="shipments/:id" element={<OrderDetailPage />} />
        <Route path="shipments/:id/edit" element={<RequestEditPage kind="OUTBOUND" />} />
        <Route path="shipments/:id/revise" element={<RequestEditPage kind="OUTBOUND" revise />} />
        <Route path="orders" element={<Navigate to="/shipments" replace />} />
        <Route path="orders/new" element={<Navigate to="/shipments/new" replace />} />
        <Route path="orders/:id" element={<RedirectToShipment />} />
        <Route path="returns" element={<ReturnsListPage />} />
        <Route path="returns/:id" element={<ReturnDetailPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
